import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Swarm } from '@lbs/swarm-client';
import * as _ from "lodash";

import Config from '@skin_config';
import OddConverter from '@common/helpers/oddConverter';
import { getAuthData } from '@common/helpers/auth';
import { useTranslation } from '@common/hooks/translation';
import { goToGame } from '@common/helpers/goToGame';
import { BetslipCommunicator, BetslipEventsEnum } from '@betslip/communicator';
import { asyncWrapper } from '@common/hocs/asyncWrapper';
import { PrintTicket } from '@common/actions/terminal';
import { GetCurrencyConfig } from '@common/helpers/selectors';
import { SelectionsContext, getSelectionsContextValue } from '@common/contexts/selections';
import { getPreferencesContextValue, PreferencesContext } from '@common/contexts/preferences';
import { lazyImport } from '@common/hocs/lazyImport';

import { getPopupDispatchers } from '@app/dev/contexts/popup';
import { hasProfitBoost } from '@app/dev/helpers/profitBoost';
import { POPUPS } from '@common/constants/popups';
import { useBalance } from '../../hooks/balance';
import { store } from '../../../index/terminalApp';
import { findByBookId } from '../../../helpers/bookingCheck';
import { UIContext } from '@app/dev/contexts/ui';
import {
  applyUser, createUserBet, logoutUser, verifyUserAmount,
} from '@app/dev/helpers/verification';
import { getBalance } from '@app/dev/helpers/balance';
import { applyMarketReplacmenets } from '@common/helpers/marketReplacments';

import './style.scss';
import { setProfitBoostInfo } from '@app/dev/actions/terminal';

const Betslip = lazyImport(() => import('@betslip'));

BetslipCommunicator.on(BetslipEventsEnum.GoToGame, goToGame);

BetslipCommunicator.on(BetslipEventsEnum.BetBooked, (bookingData) => {
  const { openPopup } = getPopupDispatchers();
  openPopup(POPUPS.PRINT_BOOKING_TICKET, { bookingData });
});

BetslipCommunicator.on(BetslipEventsEnum.BetPlaced, ({ data, verification: bisVerification, stakeAmount }) => {
  const { openPopup } = getPopupDispatchers();
  const { terminal: { verification, profitBoostPercent, playerInfo }, user } = store.getState();
  const { profile } = user;
  const balance = getBalance();
  let clientName = '';
  // Config.wheelConfig.isLoggedIn - implement isLoggedIn case heto
  if (hasProfitBoost(data, stakeAmount) && !data.is_super_bet && !data.boost_percentage && !profitBoostPercent) openPopup(POPUPS.PROFIT_BOOST_WHEEL, { stakeAmount });
  if (verification.isVerified) openPopup(POPUPS.PASSPORT_VERIFICATION_LOGOUT);
  if (bisVerification?.success) {
    createUserBet(bisVerification.data, data);
    (balance - data.amount <= 0) && logoutUser();
  }

  if (data.is_super_bet) {
    const { openPopup } = getPopupDispatchers();
    const superBetId = data.id;
    const superbetAmount = data.amount;
    const superbetPrice = data.price;
    const isCounterOffer = data.is_counter_offer;

    openPopup(POPUPS.SUPERBET, {
      disable: true,
      superBetId,
      superbetAmount,
      superbetPrice,
      isCounterOffer,
    });
  } else {
    const [{ oddsFormat }] = getPreferencesContextValue();

    const paymentPlayerInfo = playerInfo?.PersonalId ? {
      PaymentsPlayerId: playerInfo.PaymentsPlayerId, // CustomerAliasId
      PaymentsRequestId: playerInfo.PaymentsRequestId,
    } : {};

    const ticket = {
      ...data,
      ...paymentPlayerInfo,
      client_id: data.client_id || null,
      price: OddConverter(Config, data.price, oddsFormat),
      stake_tax_amount: data.stake_tax_amount ?? null,
      selection_list: data.selection_list.map((item) => ({
        ...item,
        price: OddConverter(Config, item.price, oddsFormat),
      })),
    };

    if (profile?.first_name) {
      clientName = `${profile.first_name}.${profile.last_name}`;
    }

    if (bisVerification?.success) ticket.player_name = bisVerification.data.name || '-';
    else if (clientName) ticket.player_name = clientName || '-';

    store.dispatch(PrintTicket(JSON.stringify(ticket)));
  }
});

BetslipCommunicator.on(BetslipEventsEnum.FindBetByBookId, findByBookId);

BetslipCommunicator.on(BetslipEventsEnum.BetProcessing, (data) => {
  if ('placeBetLoading' in data) {
    const [, setSelections] = getSelectionsContextValue();
    setSelections((newValue) => _.cloneDeep({ ...newValue, placeBetLoading: data.placeBetLoading }));
  }
});

BetslipCommunicator.on(BetslipEventsEnum.ProfitBoost, ({ percent, stake }) => {
  store.dispatch(setProfitBoostInfo(percent, stake));
});

const transformSelection = (selection) => (Config.sportsbook.enableTwoUP
  ? { ...selection, market: applyMarketReplacmenets(selection.market) }
  : selection);

const BetslipWrapper = () => {
  const [{ lang, oddsFormat }] = useContext(PreferencesContext);

  const user = useSelector((state) => state.user);
  const [{ theme }] = useContext(UIContext);
  const { verification, startupSettings, profitBoostPercent, profitBoostStake } = useSelector((state) => state.terminal);
  const [{ events, bookingAmount }, setSelections] = useContext(SelectionsContext);

  const t = useTranslation();

  const balance = useBalance();
  const partnerConfig = useSelector((state) => state.swarmConfigData && (state.swarmConfigData.partner || {}));

  const bonusRules = useSelector((state) => state.terminal.bonuses);
  const currencyConfig = useSelector((state) => GetCurrencyConfig(state, Config.currencySymbolRounding));

  const appSelections = useMemo(() => Object.values(events), [events]);

  const changeSelections = (selections) => {
    const eventsObj = selections.reduce((acc, item) => {
      acc[item.event.id] = item;
      return acc;
    }, {});

    setSelections((newSelections) => ({
      ...newSelections,
      events: eventsObj,
      bookingAmount: !selections.length ? null : bookingAmount,
    }));
  };

  const selectionsSorted = appSelections.sort((a, b) => b.__originalAddedOrder - a.__originalAddedOrder);

  const memberId = useSelector((state) => state.terminal.loyalCard.result) || verification.info?.clientId;

  const authData = getAuthData();

  const isVerificationDone = !startupSettings.IsPlayerVerificationEnabled || verification.isVerified;

  const betslipConfig = {
    restrictions: startupSettings
      ? {
        minBetStake: startupSettings.MinBet,
        maxBetStake: startupSettings.MaxBet,
        minimalOdd: startupSettings.MinimalOdd,
        disablePlaceBet: !isVerificationDone,
      }
      : {},

    ...Config.betslip,
    enableWinterMode: Config.main.enableWinterMode,
    isViewMode: Config.main.isViewMode,
    notifyMessages: !isVerificationDone
      ? [
        {
          type: 3, // move to enum, or make betslip enum common
          message: 'Verify yourself using your ID card or go to the cashier for assistance',
        },
      ]
      : [],
    placeBetLabel: !isVerificationDone ? 'Insert id and place bet' : '',
    hasFreeBets: !!user.profile?.has_free_bets,
    useCurrencySymbol: Config.useCurrencySymbol,
    currencySymbol: Config.currencySymbol,
    odd: Config.odd,
    showAdditionalCoin: Config.main.showAdditionalCoin,
    additionalCoinMap: Config.main.additionalCoinMap,
    additionalCoinRate: Config.main.additionalCoinRate,
    wheelConfig: Config.wheelConfig,
    verification: Config.verification,
    ..._.pick(Config.sportsbook, 'enableEventSideNameReplacement'),
  };

  return (
    <Betslip
      memberId={memberId}
      authData={authData}
      stake={bookingAmount}
      swarmInstance={Swarm.getInstance()}
      partnerConfig={partnerConfig}
      currencyConfig={{ name: authData.currency, ...currencyConfig }}
      config={betslipConfig}
      swarmConfig={Config.swarm}
      bonusRules={bonusRules || []}
      translate={t}
      freeBonuses={user.freeBonuses}
      userId={user.authData?.user_id || user.profile?.id}
      oddsFormat={oddsFormat}
      balance={balance}
      profitBoostPercent={profitBoostPercent}
      profitBoostStake={profitBoostStake}
      language={Config.swarm.languageMap?.[lang] || lang}
      selections={selectionsSorted}
      onSelectionsChange={changeSelections}
      className={`B-betslip-theme-${theme}`}
      transformSelection={transformSelection}
      verificationResponse={user.verification}
      verificationResolver={{
        login: applyUser,
        logout: logoutUser,
        validate: verifyUserAmount,
      }}
    />
  );
};

export default asyncWrapper(BetslipWrapper);
